import React, { useEffect, useState } from 'react';
import { getQueryParam, setQueryStringForCurrentUrl } from 'utils/url';
import { useFetch, useFeatureFlags } from 'hooks';
import { Button } from 'ui';
import Filter from 'components/shared/index/filters';
import Select from 'components/shared/select';
import FeatureFlag from 'components/shared/FeatureFlag';
import CombinedTagsFilter from 'components/Skills/CombinedTagsFilter';
import { routes } from 'constants/config';

export default function AdvancedSearchFilters({
  filtersChanged,
  query,
  creators,
  currentUserId,
}) {
  const [filters, setFilters] = useState({
    workspace: getQueryParam(window.location.href, 'workspace') || '',
    contentType: getQueryParam(window.location.href, 'content_type') || '',
    tags: getQueryParam(window.location.href, 'tags') || '',
    skillTags: getQueryParam(window.location.href, 'skill_tags') || '',
    startDate: getQueryParam(window.location.href, 'start_date') || '',
    endDate: getQueryParam(window.location.href, 'end_date') || '',
    status: getQueryParam(window.location.href, 'status') || '',
    creator: getQueryParam(window.location.href, 'creator') || '',
  });
  const [tagsIndexSelected, setTagsIndexSelected] = useState();
  const [skillsTagsIndexSelected, setSkillTagsIndexSelected] = useState();
  const hasSkillsEnabled = useFeatureFlags([
    'readiness-enable-skills-2-0',
    'skills',
  ]);

  const isFilterSet =
    filters.workspace ||
    filters.contentType ||
    filters.tags ||
    filters.skillTags ||
    filters.startDate ||
    filters.endDate ||
    filters.status ||
    filters.creator;

  const {
    data: workspacesData = {},
    loading: workspaceLoading,
    error,
  } = useFetch(routes.WORKSPACES.INDEX);
  const { data: tagData = {}, loading: tagLoading } = useFetch(routes.TAGS);

  const handleDateChange = dateObject => {
    const newFilters = {
      ...filters,
      startDate: dateObject.start,
      endDate: dateObject.end,
    };
    setFilters(newFilters);
  };

  const handleFilterChange =
    filterType =>
    ({ target }) => {
      const newFilters = {
        ...filters,
        [filterType]: target.value,
      };
      setFilters(newFilters);
    };

  const handleClearFilter = () => {
    setTagsIndexSelected([]);
    setSkillTagsIndexSelected([]);
    setFilters({
      workspace: '',
      contentType: '',
      tags: '',
      skillTags: '',
      startDate: '',
      endDate: '',
      status: '',
      creator: '',
    });
  };

  useEffect(() => {
    filtersChanged(filters);
    setQueryStringForCurrentUrl(
      {
        ...filters,
        query,
      },
      {
        replaceState: true,
        keepNull: false,
        enableMultiValueArray: true,
      },
    );
  }, [filters, filtersChanged, query]);

  const filterUpdateTags = tagSelectedIndexes => {
    const tags = tagSelectedIndexes.map(index => tagData.tags[index].id);
    const newFilters = {
      ...filters,
      tags: tags.length > 0 ? tags : null,
    };
    setTagsIndexSelected(tagSelectedIndexes);
    setFilters(newFilters);
  };

  // We only run this effect when the tag fetching is complete to update multi select filter
  useEffect(() => {
    const tagIndexes = [];
    if (tagData.tags?.length && filters.tags?.length) {
      filters.tags.forEach(tagId => {
        const fullTag = tagData.tags?.findIndex(
          tag => tag.id === parseInt(tagId, 10),
        );
        tagIndexes.push(fullTag);
      });
    }
    setTagsIndexSelected(tagIndexes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagData.tags]);

  function filterUpdateAllTags(selected) {
    const tags = selected.tags.map(index => tagData?.tags[index]?.id);
    const skillTags = selected.skillTags?.map(id => parseInt(id, 10));
    const newFilters = {
      ...filters,
      tags: tags.length > 0 ? tags : null,
      skillTags: skillTags.length > 0 ? skillTags : null,
    };
    setTagsIndexSelected(selected.tags);
    setSkillTagsIndexSelected(selected.skillTags);
    setFilters(newFilters);
  }

  return (
    <div className="AdvancedSearchFilters d-f trk_container_readiness_content-advanced-search-filters">
      <FeatureFlag features="workspaces">
        <Select
          value={filters.workspace}
          name="selectWorkspace"
          onChange={handleFilterChange('workspace')}
          className="AdvancedSearchFilters-Select mr-16"
          selectClass="mr-12"
          accessible
          featherIcon
        >
          <option value="">
            {t('contents.advanced_search.filters.all_workspaces')}
          </option>
          {!workspaceLoading &&
            !error &&
            workspacesData.workspaces?.map(({ id, name }) => (
              <option key={id || 0} value={id || 0}>
                {name}
              </option>
            ))}
        </Select>
      </FeatureFlag>

      <Select
        value={filters.contentType}
        name="selectContent"
        onChange={handleFilterChange('contentType')}
        className="AdvancedSearchFilters-Select mr-16"
        selectClass="mr-12"
        accessible
        featherIcon
      >
        <option value="">{t('contents.index.filters.all_content')}</option>
        <option value="lesson">{t('contents.index.filters.lessons')}</option>
        <option value="path">
          {t('contents.index.filters.paths_and_certifications')}
        </option>
        <option value="practice_container">
          {t('contents.index.filters.practice_scenarios')}
        </option>
      </Select>
      {hasSkillsEnabled && (
        <div className="AdvancedSearchFilters-Picker AdvancedSearchFilters-TagPicker mr-16">
          <CombinedTagsFilter
            data={!tagLoading && tagData.tags}
            label={{
              plural: t('contents.index.filters.tags'),
              singular: t('contents.index.filters.tag'),
            }}
            sort={{ key: 'name', descending: false }}
            selected={{
              tags: tagsIndexSelected,
              skillTags: skillsTagsIndexSelected,
            }}
            applyCallback={selected => filterUpdateAllTags(selected)}
            onKeyDown={(e, selected) => {
              if (e.key === 'Enter') {
                filterUpdateAllTags(selected);
              }
            }}
            isAdvancedSearch
            featherIcon
            whiteTheme
          />
        </div>
      )}
      {!hasSkillsEnabled && (
        <div className="AdvancedSearchFilters-Picker AdvancedSearchFilters-TagPicker mr-16">
          <Filter.MultiSelect
            data={!tagLoading && tagData.tags}
            label={{
              plural: t('contents.index.filters.tags'),
              singular: t('contents.index.filters.tag'),
            }}
            sort={{ key: 'name', descending: false }}
            selected={tagsIndexSelected}
            applyCallback={selected => filterUpdateTags(selected)}
            whiteTheme
            accessible
            featherIcon
          />
        </div>
      )}

      <div className="AdvancedSearchFilters-Picker AdvancedSearchFilters-DatePicker mr-16">
        <Filter.DatePicker
          applyCallback={dateData => handleDateChange(dateData)}
          longLabel={t('contents.advanced_search.filters.date_updated')}
          startDate={filters.startDate}
          endDate={filters.endDate}
          whiteTheme
          accessible
          featherIcon
        />
      </div>

      <Select
        value={filters.creator}
        name="selectCreator"
        onChange={handleFilterChange('creator')}
        className="AdvancedSearchFilters-Select mr-16"
        selectClass="mr-12"
        accessible
        featherIcon
      >
        <option value="">
          {t('contents.advanced_search.filters.creator')}
        </option>
        <option value={currentUserId}>{t('common.my_content')}</option>
        <option disabled>─</option>
        {creators?.map(({ id, name }) => (
          <option key={id || 'default'} value={id || 'default'}>
            {name}
          </option>
        ))}
      </Select>

      <Select
        value={filters.status}
        name="selectStatus"
        onChange={handleFilterChange('status')}
        className="AdvancedSearchFilters-Select mr-16"
        selectClass="mr-12"
        accessible
        featherIcon
      >
        <option value="">{t('contents.advanced_search.filters.status')}</option>
        <option value="published">{t('drafts.state.published')}</option>
        <option value="draft">{t('drafts.state.draft')}</option>
      </Select>

      {isFilterSet && (
        <Button
          onClick={handleClearFilter}
          onMouseDown={e => {
            // Prevents mouse click focus ring
            e.preventDefault();
          }}
          className="d-f jc-c ws-nw AdvancedSearchFilters-ClearButton"
        >
          {t('contents.advanced_search.filters.clear_filter')}
        </Button>
      )}
    </div>
  );
}
